import { BreadcrumbItem } from '../models/layout/BreadcrumbItem';
import { MenuGroupKey } from '../models/layout/MenuBarItemModel';
import { MenuPantalla, getGroupInfo } from './menuGroups';

/** Un tramo de la ruta del sub-header. */
export interface TramoRuta {
    label: string;
    /** Pantalla o registro al que lleva el tramo. */
    href?: string;
    /** Bloque y sección del menú: el tramo abre el menú lateral en ese lugar. */
    abrirMenu?: { group: MenuGroupKey; seccion?: string };
    /** Tramo de la página (la OP, la etapa…), no del menú. */
    propio?: boolean;
}

export const normalizarTexto = (texto: string): string =>
    texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/** "Calidad - Reproceso" → "Reproceso": el título de AppRoute repite el módulo. */
const tituloCorto = (headerTitle: string): string => {
    const partes = headerTitle.split(/\s[-—]\s/);
    return partes[partes.length - 1].trim();
};

/**
 * Arma la ruta de la página actual a partir del menú: Inicio / Bloque /
 * Sección / [Subgrupo] / Pantalla, más los tramos propios de la página
 * (la OP, la etapa…) que la página haya entregado con `useBreadcrumb`.
 *
 * Los tramos de la página que repiten lo que ya dice el menú se descartan,
 * así las páginas que arman su ruta a mano y las que no, se ven igual.
 */
export const armarRuta = (
    pantalla: MenuPantalla | undefined,
    pathname: string,
    itemsPagina: BreadcrumbItem[],
    headerTitle: string,
): TramoRuta[] => {
    const tramos: TramoRuta[] = [{ label: 'Inicio', href: '/' }];
    if (pathname === '/') return tramos;

    if (pantalla) {
        const bloque = pantalla.group ? getGroupInfo(pantalla.group) : undefined;
        if (bloque) tramos.push({ label: bloque.title, abrirMenu: { group: bloque.key } });
        pantalla.ruta.forEach((nombre, i) => {
            // «Gerencia / Gerencia», «Seguridad / Seguridad»: la sección que se llama
            // igual que su bloque no agrega nada.
            if (i === 0 && bloque && normalizarTexto(nombre) === normalizarTexto(bloque.title)) return;
            tramos.push({
                label: nombre,
                abrirMenu: pantalla.group ? { group: pantalla.group, seccion: pantalla.ruta[0] } : undefined,
            });
        });
        tramos.push({ label: pantalla.name, href: pantalla.href });
    }

    const conocidos = new Set(tramos.map((t) => normalizarTexto(t.label)));
    const hrefsConocidos = new Set(tramos.map((t) => t.href).filter(Boolean));
    const propios = itemsPagina
        .filter((item) => item.label && !conocidos.has(normalizarTexto(item.label)) && !(item.href && hrefsConocidos.has(item.href)))
        .map((item): TramoRuta => ({ label: item.label, href: item.href, propio: true }));
    tramos.push(...propios);

    // Ficha o sub-página sin ruta propia: el título de la página dice dónde se está.
    const enPantalla = pantalla && pathname === pantalla.href;
    if (!enPantalla && propios.length === 0 && headerTitle) {
        const titulo = tituloCorto(headerTitle);
        if (!conocidos.has(normalizarTexto(titulo))) tramos.push({ label: titulo, propio: true });
    }

    return tramos;
};

/**
 * Nombre con que la página se muestra en Volver y en Recientes: los tramos
 * propios ("19176-2026 · Evaluación") o la pantalla, y como detalle el
 * camino del menú hasta ella.
 */
export const describirPagina = (tramos: TramoRuta[]): { titulo: string; detalle?: string } => {
    const delMenu = tramos.slice(1).filter((t) => !t.propio);
    const propios = tramos.filter((t) => t.propio);
    if (tramos.length === 1) return { titulo: tramos[0].label };
    if (propios.length > 0) {
        return { titulo: propios.map((t) => t.label).join(' · '), detalle: delMenu.map((t) => t.label).join(' › ') || undefined };
    }
    return {
        titulo: delMenu[delMenu.length - 1].label,
        detalle: delMenu.slice(0, -1).map((t) => t.label).join(' › ') || undefined,
    };
};
